import { useEffect, useMemo, useState } from "react";
import {
  importSupplierTemplates,
  listSupplierTemplates,
} from "../../services/supplierTemplateApi";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

function getTemplateId(item) {
  return String(item?.id || item?._id || "").trim();
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function summarizeResult(result) {
  const imported = Number(result?.importedCount || 0);
  const conflicts = Number(result?.conflictCount || 0);
  const skipped = Number(result?.skippedCount || 0);
  if (imported > 0 && conflicts === 0) {
    return `${imported} leveranciers geimporteerd.`;
  }
  if (imported > 0 && conflicts > 0) {
    return `${imported} geimporteerd, ${conflicts} conflict(en), ${skipped} overgeslagen.`;
  }
  if (imported === 0 && conflicts > 0) {
    return `Geen import uitgevoerd door ${conflicts} conflict(en).`;
  }
  return "Geen leveranciers geimporteerd.";
}

export default function SupplierTemplateImportPanel() {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [importMode, setImportMode] = useState("skip_conflict");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [importInfo, setImportInfo] = useState("");
  const [importResult, setImportResult] = useState(null);

  async function loadTemplates({ page = 1, search = query, active = activeOnly } = {}) {
    try {
      setLoading(true);
      setLoadError("");
      const res = await listSupplierTemplates({
        page,
        limit: pagination.limit || 25,
        search,
        sourceType: "2ba",
        active: active ? "true" : undefined,
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setPagination({
        page: Number(res?.pagination?.page || page || 1),
        limit: Number(res?.pagination?.limit || pagination.limit || 25),
        total: Number(res?.pagination?.total || 0),
        totalPages: Number(res?.pagination?.totalPages || 1),
      });
    } catch (err) {
      setLoadError(err?.response?.data?.message || "Supplier templates laden mislukt");
      setItems([]);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTemplates({ page: 1, search: "", active: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleIds = useMemo(
    () => items.map((item) => getTemplateId(item)).filter(Boolean),
    [items]
  );

  const selectedCount = selectedIds.size;
  const visibleSelectedCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

  function toggleSelectOne(id, checked) {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleSelectVisible(checked) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }

  async function applySearch() {
    const next = String(queryInput || "").trim();
    setQuery(next);
    await loadTemplates({ page: 1, search: next, active: activeOnly });
  }

  async function toggleActiveFilter(checked) {
    setActiveOnly(checked);
    await loadTemplates({ page: 1, search: query, active: checked });
  }

  async function goToPage(nextPage) {
    const page = Number(nextPage || 1);
    if (!Number.isFinite(page) || page < 1) return;
    if (page > Number(pagination.totalPages || 1)) return;
    await loadTemplates({ page, search: query, active: activeOnly });
  }

  async function runImport() {
    if (importing || selectedCount === 0) return;

    try {
      setImporting(true);
      setImportError("");
      setImportInfo("");

      const result = await importSupplierTemplates({
        templateIds: Array.from(selectedIds),
        mode: importMode,
      });

      setImportResult(result);
      setImportInfo(summarizeResult(result));
      setSelectedIds(new Set());
      await loadTemplates({ page: pagination.page, search: query, active: activeOnly });
    } catch (err) {
      const details = err?.response?.data?.details;
      if (details && typeof details === "object") {
        setImportResult(details);
        setImportError(err?.response?.data?.message || "Import afgebroken door conflicts");
      } else {
        setImportError(err?.response?.data?.message || "Supplier template importeren mislukt");
      }
    } finally {
      setImporting(false);
    }
  }

  const conflicts = Array.isArray(importResult?.conflicts) ? importResult.conflicts : [];

  return (
    <section className="bg-white rounded-xl shadow p-5 space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Standaard leveranciersbibliotheek</h3>
        <p className="text-sm text-gray-600 mt-1">
          Selecteer leveranciers uit de 2BA-templatebibliotheek en importeer ze als tenant-eigen leveranciers.
        </p>
      </div>

      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{loadError}</div>
      ) : null}

      {importError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{importError}</div>
      ) : null}

      {importInfo ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          {importInfo}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto] md:items-end">
        <label className="block">
          <span className="block text-sm text-gray-600 mb-1">Zoeken</span>
          <input
            type="text"
            className="w-full border rounded p-2"
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applySearch();
              }
            }}
            placeholder="Bijv. technische unie"
          />
        </label>

        <button
          type="button"
          onClick={applySearch}
          disabled={loading}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
        >
          Zoeken
        </button>

        <button
          type="button"
          onClick={() => {
            setQueryInput("");
            setQuery("");
            loadTemplates({ page: 1, search: "", active: activeOnly });
          }}
          disabled={loading}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
        >
          Reset
        </button>

        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(event) => toggleActiveFilter(event.target.checked)}
            disabled={loading}
          />
          Alleen actief
        </label>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b bg-slate-50 text-left text-slate-600">
              <th className="px-3 py-2 w-10">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(event) => toggleSelectVisible(event.target.checked)}
                  disabled={loading || visibleIds.length === 0}
                />
              </th>
              <th className="px-3 py-2">Naam</th>
              <th className="px-3 py-2">Categorie</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Bron ID</th>
              <th className="px-3 py-2">Laatst gezien</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-4 text-slate-500" colSpan={6}>Templates laden...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-slate-500" colSpan={6}>Geen templates gevonden.</td>
              </tr>
            ) : (
              items.map((item) => {
                const id = getTemplateId(item);
                return (
                  <tr key={id || item?.sourceSupplierId} className="border-b last:border-b-0">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(id)}
                        onChange={(event) => toggleSelectOne(id, event.target.checked)}
                        disabled={!id}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900">{String(item?.name || "-")}</td>
                    <td className="px-3 py-2 text-slate-700">{String(item?.category || "-")}</td>
                    <td className="px-3 py-2 text-slate-700">
                      <div>{String(item?.defaultEmail || "-")}</div>
                      <div className="text-xs text-slate-500">{String(item?.defaultPhone || "-")}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{String(item?.sourceSupplierId || "-")}</td>
                    <td className="px-3 py-2 text-slate-700">{formatDateTime(item?.lastSeenAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="text-slate-600">
          Pagina {pagination.page} van {pagination.totalPages} · totaal {pagination.total}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goToPage(pagination.page - 1)}
            disabled={loading || pagination.page <= 1}
            className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
          >
            Vorige
          </button>
          <button
            type="button"
            onClick={() => goToPage(pagination.page + 1)}
            disabled={loading || pagination.page >= pagination.totalPages}
            className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
          >
            Volgende
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="block text-sm text-gray-600 mb-1">Import mode</span>
            <select
              className="border rounded p-2 text-sm"
              value={importMode}
              onChange={(event) => setImportMode(event.target.value)}
              disabled={importing}
            >
              <option value="skip_conflict">Skip conflicts (aanbevolen)</option>
              <option value="fail_on_conflict">Stop bij conflict</option>
            </select>
          </label>
          <button
            type="button"
            onClick={runImport}
            disabled={importing || selectedCount === 0}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {importing ? "Importeren..." : `Importeer selectie (${selectedCount})`}
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            disabled={importing || selectedCount === 0}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            Selectie leegmaken
          </button>
        </div>

        <p className="text-xs text-slate-500">
          Geselecteerd: {selectedCount} template(s), op deze pagina: {visibleSelectedCount}/{visibleIds.length}.
        </p>
      </div>

      {importResult ? (
        <div className="rounded-lg border border-slate-200 p-4 space-y-2">
          <h4 className="text-sm font-semibold text-slate-900">Laatste importresultaat</h4>
          <div className="grid gap-2 md:grid-cols-4 text-sm text-slate-700">
            <p>Aangevraagd: {Number(importResult?.requested || 0)}</p>
            <p>Geimporteerd: {Number(importResult?.importedCount || 0)}</p>
            <p>Overgeslagen: {Number(importResult?.skippedCount || 0)}</p>
            <p>Conflicts: {Number(importResult?.conflictCount || 0)}</p>
          </div>

          {conflicts.length > 0 ? (
            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-slate-600">
                    <th className="px-2 py-1.5">Template</th>
                    <th className="px-2 py-1.5">Reden</th>
                    <th className="px-2 py-1.5">Bestaande supplier</th>
                    <th className="px-2 py-1.5">Melding</th>
                  </tr>
                </thead>
                <tbody>
                  {conflicts.slice(0, 50).map((item, index) => (
                    <tr key={`${item?.templateId || "conflict"}-${index}`} className="border-b last:border-b-0">
                      <td className="px-2 py-1.5">{String(item?.name || item?.templateId || "-")}</td>
                      <td className="px-2 py-1.5">{String(item?.reason || "-")}</td>
                      <td className="px-2 py-1.5">{String(item?.existingSupplierId || "-")}</td>
                      <td className="px-2 py-1.5">{String(item?.message || "-")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
